// On Sheet and the lock (ASSESSMENT_FLOW_B7_PART2_WORDS.md items 1 to 4, MOCKUP_ASSESSMENT_PART2.html screens 1 to 3,
// approved by Robert 2026-10-29 2:46 and 2:59 PM). Pure: the walk and the sheet's records in, what is locked out.
//
// Walk gear is On Sheet once any Asset ID it makes is pushed or saved on the sheet, and not removed there
// (PUSH_BY_ROOM_B7_WORDS.md item 8, Robert 5:01 PM: push it, it says it's on the sheet; replaces the 2:06 PM "saved
// rows only"). The push is kept in the walk, so every iPad reads the same. On Sheet gear is locked: no change in the
// walk may make one of its Asset IDs disappear (a rename, a remove, a bucket taken out, a room recode). New parts
// still add. Unlock opens one card, until Field Labels is left.

import type { ClassOption } from "./asset-id";
import { walkRowsOf } from "./bring-in";
import { roomOfAssetId, type FieldSite, type Gear, type SiteRoom } from "./field-labels-site";

type Docs = Record<string, Record<string, unknown>>;

/** Asset IDs saved on the sheet and not removed. */
export function liveIds(docs: Docs | null): Set<string> {
  return new Set(Object.entries(docs ?? {}).filter(([, d]) => d.isRemoved !== true).map(([id]) => id));
}

/**
 * Asset IDs on the sheet, as Field Labels shows them: saved and live, or pushed from the walk and not marked removed
 * on the sheet. A row pushed and still waiting in the run counts; a removed row put back in the walk does not, until
 * the push restores it.
 */
export function sheetIds(docs: Docs | null, walk: FieldSite): Set<string> {
  const out = liveIds(docs);
  for (const m of Object.values(walk.pushed ?? {})) for (const id of m.ids) if (docs?.[id]?.isRemoved !== true) out.add(id);
  return out;
}

/** Gear id to the Asset IDs on the sheet it makes. Gear with none is not in the map. */
export function onSheetGear(walk: FieldSite, siteNumber: string, classes: readonly ClassOption[], docs: Docs | null): Map<string, string[]> {
  const live = sheetIds(docs, walk);
  const out = new Map<string, string[]>();
  for (const w of walkRowsOf(walk, siteNumber, classes).rows) {
    if (!live.has(w.id)) continue;
    out.set(w.gearId, [...(out.get(w.gearId) ?? []), w.id]);
  }
  return out;
}

/**
 * The saved Asset IDs a change would lose, from locked gear only: walk before and after. Empty: the change may stand.
 * Gear that is unlocked may lose its IDs: the next push carries that to the sheet.
 */
export function lockedLost(before: FieldSite, after: FieldSite, siteNumber: string, classes: readonly ClassOption[], docs: Docs | null, unlocked: ReadonlySet<string>): { id: string; gearId: string }[] {
  const live = sheetIds(docs, before);
  if (!live.size) return [];
  // Who makes each ID after the change. A saved ID now made by different gear is lost too, unlocked or not: the
  // sheet row would belong to another piece of gear (field test 2026-10-29 4:15 PM, PNL-02 renumbered to PNL-01).
  const now = new Map(walkRowsOf(after, siteNumber, classes).rows.map((w) => [w.id, w.gearId]));
  return walkRowsOf(before, siteNumber, classes)
    .rows.filter((w) => live.has(w.id) && ((!unlocked.has(w.gearId) && !now.has(w.id)) || (now.has(w.id) && now.get(w.id) !== w.gearId)))
    .map((w) => ({ id: w.id, gearId: w.gearId }));
}

/**
 * What the next push carries back to the sheet (ASSESSMENT_FLOW_B7_PART2_WORDS.md items 6 and 9). Only rows that came
 * from Field Labels (addedFrom). remove: saved, live, and the walk no longer makes them. restore: removed, and the walk
 * makes them again. A walk with no rooms carries nothing, and gear whose class is missing or off is never read as gone.
 */
export function pushChanges(walk: FieldSite, siteNumber: string, classes: readonly ClassOption[], docs: Docs | null): { remove: string[]; restore: string[] } {
  const out = { remove: [] as string[], restore: [] as string[] };
  if (!walk.rooms.length || !docs) return out;
  const { rows, skipped } = walkRowsOf(walk, siteNumber, classes);
  const made = new Set(rows.map((w) => w.id));
  const off = new Set(skipped.map((k) => k.type));
  for (const [id, d] of Object.entries(docs)) {
    if (d.addedFrom !== "Field Labels" || off.has(String(d.equipmentCode ?? ""))) continue;
    if (d.isRemoved !== true && !made.has(id)) out.remove.push(id);
    else if (d.isRemoved === true && made.has(id)) out.restore.push(id);
  }
  const order = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });
  out.remove.sort(order);
  out.restore.sort(order);
  return out;
}

/** A row the walk owns: the sheet points to Field Labels to remove, restore or rename it (item 8). */
export const fromWalk = (d: Record<string, unknown> | null | undefined): boolean => d?.addedFrom === "Field Labels";

/**
 * "00-ER1-PNL-01 comes off the Assessment Sheet on the next push." Printed ones add the door note. Empty for none.
 * Used after a remove and after Undo, so both say the same thing the same way.
 */
export function sheetOffWords(gone: readonly string[], printed: readonly string[]): string {
  if (!gone.length) return "";
  const list = (ids: readonly string[]) => (ids.length === 1 ? ids[0]! : `${ids.slice(0, -1).join(", ")} and ${ids[ids.length - 1]}`);
  const off = `${list(gone)} ${gone.length === 1 ? "comes" : "come"} off the Assessment Sheet on the next push.`;
  if (!printed.length) return off;
  const one = printed.length === 1;
  return `${off} QR label${one ? "" : "s"} printed for ${list(printed)}. Take ${one ? "it" : "them"} off the door, with the field label${one ? "" : "s"}.`;
}

/** The walk with one room in it, its marks kept: what a room's push reads (item 1). */
export function roomOnly(walk: FieldSite, room: string): FieldSite {
  return { ...walk, rooms: walk.rooms.filter((r) => r.name === room) };
}

/**
 * A room's push (items 1, 2 and 10): the removals and put backs from that room, and the ones from rooms no longer in
 * the walk, which ride along with any room's push.
 */
export function roomChanges(walk: FieldSite, room: string, siteNumber: string, classes: readonly ClassOption[], docs: Docs | null): { remove: string[]; restore: string[]; goneRemove: string[]; goneRestore: string[] } {
  const all = pushChanges(walk, siteNumber, classes, docs);
  const names = new Set(walk.rooms.map((r) => r.name));
  const here = (id: string) => roomOfAssetId(id) === room;
  const gone = (id: string) => !names.has(roomOfAssetId(id));
  return { remove: all.remove.filter(here), restore: all.restore.filter(here), goneRemove: all.remove.filter(gone), goneRestore: all.restore.filter(gone) };
}

/**
 * Item 12: the name new gear takes in a room with gear on the sheet, when the name it was given is an Asset ID the
 * sheet has held (live or removed): the next number after every one the sheet has held for that type in that room,
 * and after the room's own gear of that type. Null: the given name is free. MCCs keep their own naming.
 */
export function heldName(room: SiteRoom, g: Gear, siteNumber: string, classes: readonly ClassOption[], docs: Docs): string | null {
  if (g.type === "MCC") return null;
  const id = walkRowsOf({ rooms: [room] }, siteNumber, classes).rows.find((x) => x.gearId === g.id && !x.pos)?.id ?? "";
  if (!id || !docs[id]) return null;
  const stem = id.slice(0, id.lastIndexOf("-") + 1);
  const numOf = (x: string) => (x.startsWith(stem) && /^\d+$/.test(x.slice(stem.length)) ? Number(x.slice(stem.length)) : 0);
  const inRoom = room.gear.filter((x) => x !== g && x.type === g.type).map((x) => Number(/-(\d+)$/.exec(x.name)?.[1] ?? 0));
  const top = Math.max(0, ...Object.keys(docs).map(numOf), ...inRoom);
  const width = /-(\d+)$/.exec(g.name)?.[1]?.length ?? 2;
  return `${g.type}-${String(top + 1).padStart(width, "0")}`;
}
